import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { navLinks } from '../data/siteData'
import logo from '../assets/logo.jpg'

function DropdownMenu({ items, visible }) {
  return (
    <div className={`absolute top-full left-0 mt-1 min-w-[200px] bg-white rounded-xl shadow-xl border border-black/5 overflow-hidden transition-all duration-200 z-50 ${visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
      {items.map(item => (
        item.external
          ? <a key={item.name} href={item.external} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-3 text-sm transition-colors hover:bg-saffron-pale border-b border-black/4 last:border-0"
              style={{color:'var(--muted)'}}>
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
              {item.name}
            </a>
          : <NavLink key={item.name} to={item.to}
              className="flex items-center px-4 py-3 text-sm transition-colors border-b border-black/4 last:border-0"
              style={({ isActive }) => ({ color: isActive ? 'var(--saffron)' : 'var(--muted)', background: isActive ? 'var(--saffron-pale)' : 'white' })}>
              {item.name}
            </NavLink>
      ))}
    </div>
  )
}

export default function Navbar() {
  const loginUrl = 'https://district.sewabhartiharyana.com/signin'
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [mobileExpanded, setMobileExpanded] = useState(null)
  const location = useLocation()
  const navRef = useRef(null)
  const [currentLang, setCurrentLang] = useState('en')

  const changeLanguage = (lang) => {
    // 1. Force the cookie for Google Translate
    const cookieValue = `/en/${lang}`;
    document.cookie = `googtrans=${cookieValue}; path=/`;
    document.cookie = `googtrans=${cookieValue}; path=/; domain=${window.location.hostname}`;
    
    // 2. Try to trigger the engine directly if already loaded
    const select = document.querySelector('.goog-te-combo');
    if (select) {
      select.value = lang;
      select.dispatchEvent(new Event('change'));
      setCurrentLang(lang);
      
      // If switching to Hindi, we might need a small kick to ensure it stays
      if (lang === 'hi') {
          setTimeout(() => {
              if (currentLang !== 'hi') setCurrentLang('hi');
          }, 300);
      }
    } else {
      // 3. Fallback: If widget isn't ready, the cookie + reload is 100% reliable
      window.location.reload();
    }
  }

  useEffect(() => {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    }
    const savedLang = getCookie('googtrans');
    if (savedLang) {
      const lang = savedLang.split('/').pop();
      setCurrentLang(lang);
    }
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => { setMobileOpen(false); setOpenDropdown(null) }, [location])

  useEffect(() => {
    const handler = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <>
      {/* Topbar */}
      <div className="hidden md:flex justify-center gap-10 flex-wrap px-4 py-2 text-xs" style={{background:'var(--navy)',color:'rgba(255,255,255,0.65)'}}>
        <span className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 012 2.21 2 2 0 014 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/></svg>
          0184-2255874
        </span>
        <span className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          sewabharti1980@gmail.com
        </span>
        <span className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          Karnal, Haryana – 132001
        </span>
        <span className="flex items-center gap-2 ml-4">
          <a href="https://www.facebook.com/share/16AYhcvdp6/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
          </a>
          <a href="https://youtu.be/vplUv0K9AJs" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.4a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg>
          </a>
        </span>
        <div className="flex items-center gap-1 ml-4 border-l border-white/10 pl-4">
          <div className="mr-1 text-white/30">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>
          </div>
          <button onClick={() => changeLanguage('en')} className={`px-2 py-0.5 rounded transition-colors ${currentLang === 'en' ? 'bg-white/10 text-white' : 'hover:text-white'}`}>English</button>
          <span className="opacity-30">|</span>
          <button onClick={() => changeLanguage('hi')} className={`px-2 py-0.5 rounded transition-colors ${currentLang === 'hi' ? 'bg-white/10 text-white' : 'hover:text-white'}`}>हिन्दी</button>
        </div>
      </div>

      {/* Main nav */}
      <header ref={navRef} className="bg-white sticky top-0 z-50 transition-all duration-300" style={{boxShadow: scrolled ? '0 4px 24px rgba(13,27,62,0.10)' : '0 1px 0 rgba(0,0,0,0.06)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[68px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 shadow-sm border border-black/5">
              <img src={logo} alt="Sewa Bharti" className="w-full h-full object-cover"/>
            </div>
            <div className="hidden sm:block">
              <div className="font-bold text-[14px] leading-tight" style={{fontFamily:'Playfair Display,serif',color:'var(--navy)'}}>Sewa Bharti</div>
              <div className="text-[9px] tracking-widest uppercase" style={{color:'var(--muted)'}}>Haryana Pradesh</div>
            </div>
          </Link>

          {/* Desktop links */}
          <nav className="hidden xl:flex items-center gap-0">
            {navLinks.map(link => (
              <div key={link.name} className="relative"
                onMouseEnter={() => link.dropdown && setOpenDropdown(link.name)}
                onMouseLeave={() => setOpenDropdown(null)}>
                {link.dropdown ? (
                  <button className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap"
                    style={{color: location.pathname.startsWith(link.to) ? 'var(--saffron)' : 'var(--muted)',
                            background: location.pathname.startsWith(link.to) ? 'var(--saffron-pale)' : 'transparent'}}>
                    {link.name}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                ) : (
                  <NavLink to={link.to} end={link.to === '/'}
                    className="block px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap"
                    style={({ isActive }) => ({ color: isActive ? 'var(--saffron)' : 'var(--muted)', background: isActive ? 'var(--saffron-pale)' : 'transparent' })}>
                    {link.name}
                  </NavLink>
                )}
                {link.dropdown && <DropdownMenu items={link.dropdown} visible={openDropdown === link.name}/>}
              </div>
            ))}
          </nav>

          {/* Login + Donate + Hamburger */}
          <div className="flex items-center gap-2">
            <a href={loginUrl} target="_blank" rel="noopener noreferrer" className="hidden lg:inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 border"
              style={{color:'var(--navy)',borderColor:'rgba(13,27,62,0.16)',background:'white'}}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              Login
            </a>
            <Link to="/donate" className="hidden lg:inline-flex items-center gap-1.5 text-white px-4 py-2.5 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5"
              style={{background:'var(--saffron)',boxShadow:'0 4px 14px rgba(232,103,10,0.3)'}}>
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
              Donate
            </Link>
            <div className="flex xl:hidden items-center gap-1.5 mr-2 bg-gray-100/80 backdrop-blur-md p-1 rounded-2xl border border-black/5 shadow-inner">
               <div className="p-1.5 text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>
               </div>
               <button onClick={() => changeLanguage('en')} className={`px-3 py-1.5 text-[10px] font-black rounded-xl transition-all ${currentLang === 'en' ? 'bg-white text-saffron shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>ENG</button>
               <button onClick={() => changeLanguage('hi')} className={`px-3 py-1.5 text-[10px] font-black rounded-xl transition-all ${currentLang === 'hi' ? 'bg-white text-saffron shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>हिन्दी</button>
            </div>
            <button onClick={() => setMobileOpen(true)} className="xl:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{color:'var(--navy)'}}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16"/></svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[200] xl:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0" style={{background:'rgba(0,0,0,0.45)'}}/>
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white flex flex-col shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl overflow-hidden"><img src={logo} alt="Logo" className="w-full h-full object-cover"/></div>
                <div>
                  <div className="font-bold text-sm" style={{color:'var(--navy)'}}>Sewa Bharti</div>
                  <div className="text-[10px] tracking-wide uppercase" style={{color:'var(--muted)'}}>Haryana Pradesh</div>
                </div>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-full hover:bg-gray-100" style={{color:'var(--navy)'}}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="flex items-center justify-center gap-4 py-2 border-b border-gray-50 bg-gray-50/50">
               <button onClick={() => changeLanguage('en')} className={`text-xs font-black px-4 py-2 rounded-xl transition-all ${currentLang === 'en' ? 'bg-saffron text-white shadow-lg' : 'text-muted'}`}>ENGLISH</button>
               <button onClick={() => changeLanguage('hi')} className={`text-xs font-black px-4 py-2 rounded-xl transition-all ${currentLang === 'hi' ? 'bg-saffron text-white shadow-lg' : 'text-muted'}`}>हिन्दी</button>
            </div>
            <nav className="flex flex-col p-3 gap-0.5">
              {navLinks.map(link => (
                <div key={link.name}>
                  {link.dropdown ? (
                    <>
                      <button onClick={() => setMobileExpanded(mobileExpanded === link.name ? null : link.name)}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all text-left"
                        style={{color:'var(--text)'}}>
                        {link.name}
                        <svg className={`w-4 h-4 transition-transform ${mobileExpanded === link.name ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
                      </button>
                      {mobileExpanded === link.name && (
                        <div className="ml-4 mb-1 flex flex-col gap-0.5">
                          {link.dropdown.map(item => (
                            item.external
                              ? <a key={item.name} href={item.external} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm" style={{color:'var(--muted)'}}>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
                                  {item.name}
                                </a>
                              : <NavLink key={item.name} to={item.to} className="px-4 py-2.5 rounded-lg text-sm transition-all"
                                  style={({ isActive }) => ({ color: isActive ? 'var(--saffron)' : 'var(--muted)', background: isActive ? 'var(--saffron-pale)' : 'transparent' })}>
                                  {item.name}
                                </NavLink>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <NavLink to={link.to} end={link.to === '/'}
                      className="block px-4 py-3 rounded-xl text-sm font-medium transition-all"
                      style={({ isActive }) => ({ color: isActive ? 'var(--saffron)' : 'var(--text)', background: isActive ? 'var(--saffron-pale)' : 'transparent' })}>
                      {link.name}
                    </NavLink>
                  )}
                </div>
              ))}
            </nav>
            <div className="p-4 border-t border-gray-100 mt-auto">
              <a href={loginUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-3 rounded-full font-semibold text-sm mb-2 border"
                style={{color:'var(--navy)',borderColor:'rgba(13,27,62,0.16)',background:'white'}}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                Login
              </a>
              <Link to="/donate" className="flex items-center justify-center gap-2 w-full text-white py-3 rounded-full font-semibold text-sm"
                style={{background:'var(--saffron)'}}>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                Donate Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
